/** Side of the header photo, in pdf points. Shared with the header layout. */
export const HEADER_PHOTO_SIZE = 45

/** Raster the photo above its display size so it stays crisp in print. */
const PHOTO_SCALE = 3

/**
 * How long the header photo gets before the export gives up on it. The risk
 * assessment PDF had no network dependency at all before the photo was added,
 * so an unbounded fetch would turn a previously instant download into one that
 * can hang — on a PEEP, at the moment someone needs it most.
 */
const PHOTO_TIMEOUT_MS = 5000

const decodeImage = (src: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image()
    image.onload = () => resolve(image)
    image.onerror = () => reject(new Error('Image could not be decoded'))
    image.src = src
  })

const context2d = (canvas: HTMLCanvasElement): CanvasRenderingContext2D => {
  const context = canvas.getContext('2d')
  if (!context) throw new Error('Canvas 2d context is unavailable')
  return context
}

const toDataUrl = (image: HTMLImageElement): string => {
  const canvas = document.createElement('canvas')
  canvas.width = image.width
  canvas.height = image.height

  context2d(canvas).drawImage(image, 0, 0)
  return canvas.toDataURL()
}

/**
 * Draws the photo into a circular frame at `size` points.
 *
 * pdfmake cannot round an image's corners, so the mask has to be baked into the
 * bytes before it ever reaches the document. The square is filled white first:
 * a service user's photo is arbitrary, and an opaque backdrop keeps the edge
 * clean on the page rather than depending on how pdfkit composites alpha.
 */
const toCircularDataUrl = (image: HTMLImageElement, size: number): string => {
  const pixels = size * PHOTO_SCALE
  const canvas = document.createElement('canvas')
  canvas.width = pixels
  canvas.height = pixels

  const context = context2d(canvas)
  context.fillStyle = '#ffffff'
  context.fillRect(0, 0, pixels, pixels)

  const radius = pixels / 2
  context.beginPath()
  context.arc(radius, radius, radius, 0, Math.PI * 2)
  context.closePath()
  context.clip()

  // Cover-crop to a square around the centre, so a portrait or landscape photo
  // is not squashed into an ellipse by the circular frame.
  const side = Math.min(image.width, image.height)
  context.drawImage(
    image,
    (image.width - side) / 2,
    (image.height - side) / 2,
    side,
    side,
    0,
    0,
    pixels,
    pixels
  )

  return canvas.toDataURL()
}

/**
 * pdfmake needs image bytes inline, and the photo URLs are presigned S3 links
 * the page has already rendered in plain `<img>` tags. Those loads send no
 * Origin, so S3 skips CORS evaluation and the cached response has no
 * Access-Control-Allow-Origin — re-reading the same URL through a crossOrigin
 * image is then blocked by that cached response rather than by S3. A
 * cache-busting param is not an option because SigV4 signs the query string, so
 * fetch the bytes past the cache instead and decode from a blob URL, which
 * leaves the canvas untainted.
 *
 * The object URL is revoked only once the canvas has been drawn, so the decoded
 * bitmap is never read back after its source has gone.
 */
const withDecodedImage = async <T>(
  url: string,
  draw: (image: HTMLImageElement) => T,
  signal?: AbortSignal
): Promise<T> => {
  const response = await fetch(url, {
    cache: 'reload',
    ...(signal ? { signal } : {})
  })
  if (!response.ok) {
    throw new Error(`Image request failed with status ${response.status}`)
  }

  const objectUrl = URL.createObjectURL(await response.blob())
  try {
    return draw(await decodeImage(objectUrl))
  } finally {
    URL.revokeObjectURL(objectUrl)
  }
}

/**
 * Runs `attempt` under a deadline. The signal cancels the fetch; the race also
 * bounds decoding, which an AbortSignal does not reach. Rejecting on expiry
 * lands on the caller's existing failure path.
 */
const withDeadline = async <T>(
  attempt: (signal: AbortSignal) => Promise<T>,
  timeoutMs: number
): Promise<T> => {
  const controller = new AbortController()
  let timer: ReturnType<typeof setTimeout> | undefined

  const expiry = new Promise<never>((_resolve, reject) => {
    timer = setTimeout(() => {
      controller.abort()
      reject(new Error(`Image request timed out after ${timeoutMs}ms`))
    }, timeoutMs)
  })

  try {
    return await Promise.race([attempt(controller.signal), expiry])
  } finally {
    clearTimeout(timer)
  }
}

export const loadImageAsDataUrl = (url: string): Promise<string> =>
  withDecodedImage(url, toDataUrl)

export const loadImageAsCircularDataUrl = (
  url: string,
  size: number = HEADER_PHOTO_SIZE,
  timeoutMs: number = PHOTO_TIMEOUT_MS
): Promise<string> =>
  withDeadline(
    (signal) =>
      withDecodedImage(url, (image) => toCircularDataUrl(image, size), signal),
    timeoutMs
  )
